import React, { useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Form } from 'antd';
import { FileUploadModal } from '../../../common';
import { useTranslation } from 'react-i18next';
import { useUploadFileMutation } from '../../../../redux/services/filesApi';
import useTinyMceErrorReporting from '../../../../hooks/useTinyMceErrorReporting.jsx';
import tinymcePackage from 'tinymce/package.json';
import tinymceI18nPackage from 'tinymce-i18n/package.json';

// TinyMCE files in /public/tinymce have fixed URLs and production caches them for years,
// so the version in the query makes browsers download them again after a TinyMCE update
const TINYMCE_CACHE_SUFFIX = `v=${tinymcePackage.version}-${tinymceI18nPackage.version}`;
import editorContentCss from './editorContent.css?inline';
import richContentCss from '../../../common/styles/RichContent.sass?inline';

const FormTextEditor = ({
  label,
  name,
  id,
  rules,
  height = undefined,
  imgUploadDirectory = undefined,
  fileUploadDirectory = undefined,
  allowedTypes = undefined,
  enableCustomFileName = false,
  disabled = false,
  ...rest
}) => {

  const normalizedRules = Array.isArray(rules) ? rules : rules ? [rules] : [];
  // We don't need the alt validation in FormTextEditor anymore
  // since FormWrapper handles it with a modal dialog

  return (
    <Form.Item
      label={label}
      name={name}
      rules={normalizedRules}
    >
      <TextEditorWrapper
        id={id}
        height={height}
        imgUploadDirectory={imgUploadDirectory}
        fileUploadDirectory={fileUploadDirectory}
        allowedTypes={allowedTypes}
        enableCustomFileName={enableCustomFileName}
        disabled={disabled}
        {...rest}
      />
    </Form.Item>
  );
};

const TextEditorWrapper = ({
  height = '700px',
  id,
  imgUploadDirectory = undefined,
  fileUploadDirectory = undefined,
  allowedTypes = undefined,
  enableCustomFileName = false,
  disabled = false,
  contentClassName = '', // e.g. 'rich-content rich-content--steps' so the editor matches the rendered page
  value, // This is used by Antd Form.Item to bind values
  onChange, // This is used by Antd Form.Item to bind values
}) => {
  const [editor, setEditor] = useState(undefined); // Editor tu treba nechat ak by bolo potrebne nieco custom pridat
  const [uploadOpen, openUpload] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const { t } = useTranslation();

  const [uploadFile] = useUploadFileMutation();
  const { onInit, onScriptsLoadError, bindEditorErrorEvents } =
    useTinyMceErrorReporting();

  const editorNode = (
    <Editor
      tinymceScriptSrc={`/tinymce/tinymce.min.js?${TINYMCE_CACHE_SUFFIX}`}
      onInit={onInit}
      onScriptsLoadError={onScriptsLoadError}
      //apiKey={`${import.meta.env.VITE_TINYMCE_API_KEY}`}
      value={value}
      disabled={disabled}
      init={{
        // valid_children:'+body[style]', // Ak je povolená značka <style>, je možné rozbiť formátovanie celého portálu
        // Povolíme <i> len s class atribútom a <a> s href (vrátane mailto:)
        extended_valid_elements: 'i[class],a[href|target|rel|class|title]',

        relative_urls: false, // zakáže plne relatívne URL (napr. "images/x.jpg")
        remove_script_host: true, // odstráni hostname (napr. https://domain.com)
        document_base_url: '/', // aby cesty začínali lomkou '/'
        convert_urls: true, // zapne konverziu podľa vyššie uvedeného

        // Povolenie pre <a> s ikonami
        valid_children: '+a[i]', // Povolí <i> vo vnútri <a>

        // Povolenie prázdnych <i> s class, ale iné prázdne tagy sa môžu mazať
        remove_empty: true, // ponechá <i class="...">, ale odstráni <i></i>
        sandbox_iframes: false,
        promotion: false,
        // self-hosted TinyMCE under the open source license (no evaluation mode warning)
        license_key: 'gpl',
        // appended to every plugin, theme, model, icons, language and skin URL loaded by TinyMCE
        cache_suffix: `?${TINYMCE_CACHE_SUFFIX}`,
        language: 'sk',
        language_url: '/tinymce/langs/sk.js',
        //paste_data_images: true,
        skin: 'oxide',
        plugins:
          'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help quickbars emoticons',
        //content_css: '/theme.css',
        paste_data_images: false,
        //paste_as_text: true,
        paste_webkit_styles: '',
        paste_postprocess: function (plugin, args) {
          // Dočasný wrapper na manipuláciu s DOM
          const wrapper = document.createElement('div');
          wrapper.innerHTML = args.node.innerHTML;

          // Rekurzívne odstráni style a class (s výnimkou class na <i>)
          const cleanAttributes = (el) => {
            if (el.nodeType === 1) {
              // element
              // vždy odstráň inline štýly
              el.removeAttribute('style');
              // zachovaj class na <i>, inak odstráň class atribút
              if (el.nodeName.toLowerCase() !== 'i') {
                el.removeAttribute('class');
              }
            }

            // Odstráň HTML komentáre
            for (let i = el.childNodes.length - 1; i >= 0; i--) {
              const child = el.childNodes[i];
              if (child.nodeType === 8) {
                // komentár
                el.removeChild(child);
              } else {
                cleanAttributes(child);
              }
            }
          };

          cleanAttributes(wrapper);

          // Ensure mailto links are not removed: if an <a href="mailto:"> has neither text nor element children, fill text with the email
          const mailtoAnchors =
            wrapper.querySelectorAll('a[href^="mailto:"]');
          mailtoAnchors.forEach((a) => {
            const href = a.getAttribute('href') || '';
            const email = href.replace(/^mailto:/i, '');
            const hasElementChildren = a.children && a.children.length > 0;
            if (!hasElementChildren && a.textContent.trim() === '' && email) {
              a.textContent = email;
            }
          });

          // Výsledok vlož späť
          args.node.innerHTML = wrapper.innerHTML;
        },
        toolbar1:
          'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | forecolor backcolor removeformat | ',
        toolbar2:
          'numlist bullist | alignleft aligncenter alignright alignjustify | table | fileImportButton link imageUploadButton media | code | help',
        height: height,
        setup: (editor) => {
          bindEditorErrorEvents(editor);

          // add paper clip icon
          editor.ui.registry.addIcon(
            'paperClip',
            '<svg height="24px" width="24px" xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 1024 1024"><path d="M779.3 196.6c-94.2-94.2-247.6-94.2-341.7 0l-261 260.8c-1.7 1.7-2.6 4-2.6 6.4s.9 4.7 2.6 6.4l36.9 36.9a9 9 0 0 0 12.7 0l261-260.8c32.4-32.4 75.5-50.2 121.3-50.2s88.9 17.8 121.2 50.2c32.4 32.4 50.2 75.5 50.2 121.2 0 45.8-17.8 88.8-50.2 121.2l-266 265.9-43.1 43.1c-40.3 40.3-105.8 40.3-146.1 0-19.5-19.5-30.2-45.4-30.2-73s10.7-53.5 30.2-73l263.9-263.8c6.7-6.6 15.5-10.3 24.9-10.3h.1c9.4 0 18.1 3.7 24.7 10.3 6.7 6.7 10.3 15.5 10.3 24.9 0 9.3-3.7 18.1-10.3 24.7L372.4 653c-1.7 1.7-2.6 4-2.6 6.4s.9 4.7 2.6 6.4l36.9 36.9a9 9 0 0 0 12.7 0l215.6-215.6c19.9-19.9 30.8-46.3 30.8-74.4s-11-54.6-30.8-74.4c-41.1-41.1-107.9-41-149 0L463 364 224.8 602.1A172.22 172.22 0 0 0 174 724.8c0 46.3 18.1 89.8 50.8 122.5 33.9 33.8 78.3 50.7 122.7 50.7 44.4 0 88.8-16.9 122.6-50.7l309.2-309C824.8 492.7 850 432 850 367.5c.1-64.6-25.1-125.3-70.7-170.9z"/></svg>',
          );

          editor.ui.registry.addButton('fileImportButton', {
            icon: 'paperClip',
            tooltip: t('Pridať súbor'),
            onAction: () => {
              const text = editor.selection.getContent({ format: 'text' });
              setSelectedText(text);
              openUpload(true);
            },
          });

          editor.ui.registry.addButton('imageUploadButton', {
            tooltip: t('Pridať obrázok'),
            icon: 'image',
            onAction: () => {
              editor.execCommand('mceImage');

              setTimeout(() => {
                let element = document.querySelectorAll(
                  '.tox-dialog__body-nav-item',
                )[1];
                if (element) {
                  element.click();
                }
              }, 0);
            },
          });

          editor.on('init', () => {
            const body = editor.getBody();

            const observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                  // Zaujíma nás samotný <img>
                  if (node.nodeName === 'IMG') {
                    setTimeout(() => {
                      // Ak rodič <img> nie je <p>, obalíme ho do <p>
                      if (node.parentNode.nodeName !== 'P') {
                        const p = editor.dom.create('p', {}, '');
                        node.parentNode.insertBefore(p, node);
                        p.appendChild(node);
                      }

                      // Skontrolujeme, či za tým <p> je prázdny odstavec, ak nie, pridáme ho
                      const pNode = node.parentNode;
                      const next = pNode.nextSibling;
                      if (
                        !next ||
                        next.nodeName !== 'P' ||
                        next.innerHTML.trim() !== '<br>'
                      ) {
                        const newP = editor.dom.create('p', {}, '<br>');
                        pNode.parentNode.insertBefore(newP, next);
                      }

                      // Presunieme kurzor do nového odstavca
                      const newP = node.parentNode.nextSibling;
                      if (newP) {
                        const rng = editor.dom.createRng();
                        rng.setStart(newP, 0);
                        rng.collapse(true);
                        editor.selection.setRng(rng);
                        editor.focus();
                      }
                    }, 100); // malý delay, aby TinyMCE dokončil svoje vnútorné úpravy
                  }
                });
              });
            });

            observer.observe(body, {
              childList: true,
              subtree: true,
            });
          });

          setEditor(editor);
        },
        entity_encoding: 'raw',
        image_dimensions: false,
        object_resizing: true,
        image_class_list: [{ title: 'Responsive', value: 'img-responsive' }],
        menubar: '',
        //table_class_list: [{ title: 'Responsive', value: 'tinymce-tables' }],
        //convert_urls: false,
        images_upload_handler: (blobInfo) =>
          new Promise((resolve, reject) => {
            uploadFile({
              file: new File([blobInfo.blob()], blobInfo.filename()),
              directory: imgUploadDirectory,
            })
              .unwrap()
              .then(({ location }) => {
                resolve(location);
              })
              .catch((error) => reject(error));
          }),
        body_class: `tinymce-content ${contentClassName}`.trim(),
        content_style: contentClassName
          ? `${editorContentCss}\n${richContentCss}`
          : editorContentCss,
        //     'background-color: #15489EFF; /* Blue */' +
        //     'color: white;' +
        //   '}',
        table_use_colgroups: false,
      }}
      onEditorChange={(newContent) => {
        onChange(newContent);
      }}
    />
  );

  return (
    <>
      <span className={'idSpanScroolToError'} id={id} />
      {editorNode}

      <FileUploadModal
        toggle={() => openUpload(false)}
        allowedTypes={allowedTypes}
        uploadDirectory={fileUploadDirectory}
        onSuccessUpload={(fileResponse) => {
          const fallbackText = fileResponse.old_name
            ? fileResponse.old_name.substring(
                0,
                fileResponse.old_name.lastIndexOf('.'),
              )
            : fileResponse.location;

          const linkText =
            fileResponse.customName || selectedText || fallbackText;

          const hrefOfFile = `<a href="${fileResponse.location}" target="_blank">${linkText}</a>`;

          editor.insertContent(hrefOfFile);
          openUpload(false);
          setSelectedText('');
        }}
        enableCustomFileName={enableCustomFileName}
        open={uploadOpen}
        initialText={selectedText}
      />
    </>
  );
};

export default FormTextEditor;
